// Everything companions do on their own, in one list. The decisions (what to do next, how to
// get somewhere, who plays together, which dance move) ask can(me, id) before picking anything,
// and the settings menu shows this list, so a new action needs an entry here and nothing else
// to be switchable.
//
// Whether a companion does something: the owner's setting if there is one (me.does), else the
// design's default ("does" in design.json), else the action's own default. An action the design
// can't do (no bed, nothing to open up) or never does ("no" in its "moves") is never on and
// doesn't show in the menu.
//
// Walking, hopping and choosing spots always work: without them a companion couldn't move.
//
// A design's traits say how it does the basic things ("moves" in design.json):
//   "walk":   "walk" (shuffles sideways, facing you), "profile" (turns and walks like a Minecraft
//             mob), "hop", "fly", "roll" or "none" (can't walk: it uses its own travel instead), or
//             an ability with a walk of its own ("waddle": a penguin rocking from side to side)
//   "travel": its own ways to get somewhere far (abilities: "fly", "teleport", "dig", ...). They
//             replace the general ways (its world's: see worldOf), which then start switched off.
//   "no":     things it never does at all, never offered: ways it can't travel (a Ghast has no
//             elytra), things that don't suit it (animals don't lean on walls)
//   "attack": how it fights a pest (a bug, a glitch, an endermite): "sword", "flee" (runs and
//             others help), or an ability
//   "sleep":  "bed", "sit" (nightcap), "curl", "lie", "float", or an ability ("dig", "shell")
//   "idle":   its own things to do now and then while it rests (abilities: "graze", ...)
//   "hop":    its own way of doing every small jump, an ability ("bounce": a slime's squishy hop)
//   "pace":   how fast it walks next to the others (0.6: the Iron Golem's heavy steps)
//   "weapon": the item its attack ability swings ("goldSword" for "swing"; see the ability)
//   "pointer": its own way to play with the pointer, an ability (pointer.js; Neko chasing it)

import { ability } from './abilities.js';

export const walkStyle = design => design.moves?.walk || 'walk';
export const ownTravel = design => design.moves?.travel || [];
export const ownIdle = design => design.moves?.idle || [];
export const attackStyle = design => design.moves?.attack || 'sword';
export const sleepStyle = design => design.moves?.sleep || (design.bed ? 'bed' : 'sit');

export const GROUPS = {
  travel: 'Getting around',
  idle: 'On its own',
  sleep: 'Sleeping',
  together: 'Together',
  dance: 'Dance moves',
};

const canOpen = design => !!design.body.variants?.open;
const fights = design => attackStyle(design) !== 'flee';
// Which world a companion comes from: Minecraft mobs travel the Minecraft way, everyone else
// (Clawd, the apps, the mascots) the desktop way.
export const worldOf = design => (design.group === 'Minecraft' ? 'minecraft' : 'desktop');
// A general way to travel, the one a companion's own ways replace. A way with a world is only
// for companions from that world; each Minecraft way has a desktop counterpart.
const general = (id, label, world, extra = {}) => ({ id, group: 'travel', label, general: true, world, ...extra });

export const ACTIONS = [
  general('elytra', 'Elytra and rockets', 'minecraft'),
  general('jetpack', 'Jetpack', 'desktop'),
  general('pearl', 'Ender pearl', 'minecraft'),
  general('hook', 'Grappling hook', 'desktop'),
  { id: 'endermite', group: 'travel', label: 'Endermites', needs: design => possible(design, 'pearl') },   // a pearl can let one out
  general('portal', 'Nether portal', 'minecraft'),
  general('wormhole', 'Wormhole', 'desktop'),
  general('parachute', 'Parachute'),        // for everyone
  general('slime', 'Slime block', 'minecraft'),

  { id: 'lounge', group: 'idle', label: 'Lounges' },
  { id: 'lean', group: 'idle', label: 'Leans on walls' },
  { id: 'text', group: 'idle', label: 'Sits on text' },
  { id: 'sunglasses', group: 'idle', label: 'Sunglasses' },
  { id: 'chair', group: 'idle', label: 'Deck chair' },
  { id: 'think', group: 'idle', label: 'Thinks' },
  { id: 'pests', group: 'idle', label: 'Bugs and glitches' },   // they turn up for it, and it helps others with theirs
  { id: 'babies', group: 'idle', label: 'Has babies', needs: design => !!design.babies },
  // Pointer play (pointer.js): when the pointer idles near it, it plays with it its own way ("pointer"
  // in its "moves") or the shared one. On only where it fits ("does" in the design: Neko, Cursor, ...).
  { id: 'pointer', group: 'idle', label: 'Plays with the pointer', on: false },

  { id: 'nap', group: 'sleep', label: 'Naps when you\'re away' },
  { id: 'bed', group: 'sleep', label: 'Sleeps in a bed', needs: design => sleepStyle(design) === 'bed' || !!ability(sleepStyle(design))?.bed },   // or its own way of sleeping uses one (a cat curls up on it)

  // Games have a part each companion can play; the menu shows a game's parts on one row ("part").
  { id: 'party.host', group: 'together', label: 'Throws parties', part: 'Throws them', on: false },
  { id: 'party.join', group: 'together', label: 'Joins parties', part: 'Joins in' },
  { id: 'basketball.throw', group: 'together', label: 'Shoots in file basketball', part: 'Shoots', on: false },
  { id: 'basketball.catch', group: 'together', label: 'Catches in file basketball', part: 'Catches', needs: canOpen },
  // Fights (fights.js): a duel with another that fights, now and then, and rarely turning evil.
  // Only for companions with an attack (not those that run from pests).
  { id: 'fight.duel', group: 'together', label: 'Fights others', part: 'Fights', needs: fights },
  { id: 'fight.evil', group: 'together', label: 'Can turn evil', part: 'Can turn evil', on: false, needs: fights },

  { id: 'dance.bounce', group: 'dance', label: 'Bounce' },
  { id: 'dance.wave', group: 'dance', label: 'Wave' },
  { id: 'dance.sway', group: 'dance', label: 'Sway' },
  { id: 'dance.step', group: 'dance', label: 'Step' },
  { id: 'dance.turn', group: 'dance', label: 'Turn' },
  { id: 'dance.jumpy', group: 'dance', label: 'Jumpy' },
  { id: 'dance.spin', group: 'dance', label: 'Spin', needs: design => design.moves?.walk === 'roll' },
  { id: 'dance.chomp', group: 'dance', label: 'Chomp', needs: canOpen },
  { id: 'dance.pulse', group: 'dance', label: 'Pulse', needs: design => !!design.body.variants?.pulse },
];

const BY_ID = new Map(ACTIONS.map(a => [a.id, a]));

// An action by id: one from the list, or one of the design's own ways to travel or things to do.
function actionOf(design, id) {
  if (BY_ID.has(id)) return BY_ID.get(id);
  if (ownTravel(design).includes(id)) return { id, group: 'travel', label: ability(id)?.label || id, own: true };
  if (ownIdle(design).includes(id)) return { id, group: 'idle', label: ability(id)?.label || id, own: true };
  return null;
}

// Whether a design could do it at all (has a bed, can open up, is from the way's world, doesn't
// say it never does it), whatever the settings say.
export function possible(design, id) {
  const action = actionOf(design, id);
  return !!action && !design.moves?.no?.includes(id) && (!action.needs || action.needs(design))
    && (!action.world || action.world === worldOf(design));
}

// The actions a design can do at all, in the list's order, its own ways to travel first and its
// own things to do last.
export const actionsFor = design => [...ownTravel(design).map(id => actionOf(design, id)), ...ACTIONS.filter(a => possible(design, a.id)),
  ...ownIdle(design).map(id => actionOf(design, id))];

// On unless set otherwise: a general way to travel starts off for a companion with its own ways.
const onByDefault = (design, action) => (action.general && ownTravel(design).length ? false : action.on ?? true);

// Does this companion do it (by default, or as the owner set it)?
export function can(me, id) {
  const action = actionOf(me.design, id);
  if (!action) throw new Error(`No action "${id}" in the catalog`);
  if (!possible(me.design, id)) return false;
  return me.does?.[id] ?? me.design.does?.[id] ?? onByDefault(me.design, action);
}

// What it would do without the owner's settings.
export function canByDefault(design, id) {
  if (!possible(design, id)) return false;
  return design.does?.[id] ?? onByDefault(design, actionOf(design, id));
}
